import { useState, useRef, useEffect, useCallback } from 'react';
import { Barcode, Scan, CheckCircle2, Zap, Volume2, VolumeX } from 'lucide-react';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';

interface QuickBarcodeScannerInputProps {
    value?: string;
    onChange?: (value: string) => void;
    onScan: (barcode: string) => void;
    placeholder?: string;
    autoFocus?: boolean;
    label?: string;
    className?: string;
    helperText?: string;
    size?: 'sm' | 'md' | 'lg';
}

export function QuickBarcodeScannerInput({
    value = '',
    onChange,
    onScan,
    placeholder = 'Scan barcode or type & hit Enter...',
    autoFocus = false,
    label,
    className,
    helperText,
    size = 'md',
}: QuickBarcodeScannerInputProps) {
    const [inputValue, setInputValue] = useState(value);
    const [isScannedFlash, setIsScannedFlash] = useState(false);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const inputRef = useRef<HTMLInputElement>(null);

    // Track rapid keystrokes to detect hardware barcode scanner
    const lastKeyTimeRef = useRef<number>(0);
    const barcodeBufferRef = useRef<string>('');

    // Keep internal state synced if external value changes
    useEffect(() => {
        setInputValue(value);
    }, [value]);

    // Audio synthesizer for scan beep
    const playBeep = useCallback((type: 'success' | 'error' = 'success') => {
        if (!soundEnabled) return;
        try {
            const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
            if (!AudioContext) return;
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            osc.connect(gain);
            gain.connect(ctx.destination);

            if (type === 'success') {
                osc.frequency.setValueAtTime(1400, ctx.currentTime);
                osc.frequency.setValueAtTime(1800, ctx.currentTime + 0.08);
                gain.gain.setValueAtTime(0.12, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.18);
                osc.start(ctx.currentTime);
                osc.stop(ctx.currentTime + 0.18);
            } else {
                osc.frequency.setValueAtTime(350, ctx.currentTime);
                gain.gain.setValueAtTime(0.15, ctx.currentTime);
                gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.25);
                osc.start(ctx.currentTime);
                osc.stop(ctx.currentTime + 0.25);
            }
        } catch {
            // Audio context not allowed without interaction, fail gracefully
        }
    }, [soundEnabled]);

    const triggerScanSuccess = useCallback((code: string) => {
        const trimmed = code.trim();
        if (!trimmed) return;

        playBeep('success');
        setIsScannedFlash(true);
        setTimeout(() => setIsScannedFlash(false), 900);

        onScan(trimmed);
        if (onChange) onChange(trimmed);
        setInputValue(trimmed);
    }, [playBeep, onScan, onChange]);

    // Hardware Scanner Wedge Detection
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const now = Date.now();
        const diff = now - lastKeyTimeRef.current;
        lastKeyTimeRef.current = now;

        if (e.key === 'Enter') {
            e.preventDefault();
            if (inputValue.trim()) {
                triggerScanSuccess(inputValue);
            }
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setInputValue(val);
        if (onChange) onChange(val);
    };

    return (
        <div className={clsx('relative', className)}>
            {label && (
                <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                        {label}
                    </label>
                    <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                        <Zap className="w-3 h-3 text-emerald-500 animate-pulse" />
                        <span>Scanner Active</span>
                        <button
                            type="button"
                            onClick={() => setSoundEnabled(!soundEnabled)}
                            className="p-0.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded text-slate-400 hover:text-slate-600"
                            title={soundEnabled ? 'Mute beep' : 'Enable beep'}
                        >
                            {soundEnabled ? <Volume2 className="w-3 h-3 text-emerald-600" /> : <VolumeX className="w-3 h-3 text-slate-400" />}
                        </button>
                    </div>
                </div>
            )}

            <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Barcode className={clsx('transition-colors', isScannedFlash ? 'text-emerald-500' : 'text-slate-400', size === 'sm' ? 'w-4 h-4' : 'w-5 h-5')} />
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    autoFocus={autoFocus}
                    value={inputValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    className={clsx(
                        'w-full font-mono transition-all duration-200 border-2 rounded-xl focus:outline-none focus:ring-2',
                        size === 'sm' ? 'pl-9 pr-16 py-2 text-xs' : 'pl-11 pr-20 py-2.5 text-sm',
                        isScannedFlash
                            ? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30 text-emerald-900 ring-2 ring-emerald-500/20'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:border-emerald-500 focus:ring-emerald-500/20'
                    )}
                />

                <div className="absolute inset-y-0 right-1 flex items-center pr-2 gap-1">
                    <AnimatePresence>
                        {isScannedFlash ? (
                            <motion.span
                                initial={{ scale: 0.5, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.5, opacity: 0 }}
                                className="flex items-center gap-1 text-[11px] font-bold text-emerald-600 bg-emerald-100 dark:bg-emerald-900/60 px-2 py-0.5 rounded-lg"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                                Scanned
                            </motion.span>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    if (inputValue.trim()) {
                                        triggerScanSuccess(inputValue);
                                    } else {
                                        inputRef.current?.focus();
                                    }
                                }}
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 rounded-lg transition-colors border border-emerald-200/60"
                                title="Click to trigger scan action"
                            >
                                <Scan className="w-3.5 h-3.5" />
                                <span>Scan</span>
                            </button>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {helperText && (
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                    {helperText}
                </p>
            )}
        </div>
    );
}
